import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { NAV } from "@/constants/testIds";
import { Menu, X } from "lucide-react";
import { ZInCircle } from "@/components/logos/Marks";

const LOGIN_URL = "https://dashboard.zaplee.ai/";

const links = [
  { id: "features", label: "Features", testId: NAV.linkFeatures },
  { id: "product", label: "Product", testId: NAV.linkProduct },
  { id: "how-it-works", label: "How it works", testId: NAV.linkHowItWorks },
  { id: "faq", label: "FAQ", testId: NAV.linkFaq },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/75 border-b border-zinc-200"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between">
        <a
          href="#top"
          data-testid={NAV.logo}
          className="flex items-center gap-2.5 group"
        >
          <ZInCircle variant="monolith" size="h-9 w-9" />
          <span className="font-display text-lg font-semibold tracking-tight text-zinc-950">
            Zaplee<span className="text-[#0047FF]">.ai</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={l.testId}
              onClick={() => goTo(l.id)}
              className="text-sm text-zinc-600 hover:text-zinc-950 transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            data-testid={NAV.ctaLogin}
            href={LOGIN_URL}
            className="hidden md:inline-flex items-center rounded-full border border-[#0047FF] px-5 h-10 text-sm font-medium text-[#0047FF] hover:bg-[#0047FF] hover:text-white transition-colors"
          >
            Login
          </a>
          <Button
            data-testid={NAV.ctaDemo}
            onClick={() => goTo("contact")}
            className="hidden md:inline-flex bg-zinc-950 text-white hover:bg-zinc-800 rounded-full px-5 h-10"
          >
            Book a Demo
          </Button>
          <button
            className="md:hidden p-2 rounded-md text-zinc-700 hover:bg-zinc-100"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-zinc-200 bg-white">
          <div className="px-6 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`${l.testId}-mobile`}
                onClick={() => goTo(l.id)}
                className="text-left py-2 text-zinc-800"
              >
                {l.label}
              </button>
            ))}
            <Button
              onClick={() => goTo("contact")}
              className="mt-2 bg-zinc-950 text-white hover:bg-zinc-800 rounded-full"
            >
              Book a Demo
            </Button>
            <a
              data-testid={`${NAV.ctaLogin}-mobile`}
              href={LOGIN_URL}
              className="inline-flex items-center justify-center rounded-full border border-[#0047FF] h-10 text-sm font-medium text-[#0047FF] hover:bg-[#0047FF] hover:text-white transition-colors"
            >
              Login
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
